import { Inject, Injectable } from '@angular/core';
import { CancellationTokenHandler } from 'src/app/common/cancellation/cancellationtoken-handler';
import { ICancellationTokenHandler } from 'src/app/common/contracts/cancellation/cancellationtoken-handler';

import makeDebug from '../../../../makeDebug';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement, SyncElementResult } from '../contracts/sync/sync-element';
import { TokenHandler as Token } from '../contracts/sync/sync-endpoints';

const debug = makeDebug('sync:token-handler');

/**
 * Gives every sync run a fresh set of cancellation tokens and ends the work of the previous run.
 *
 * "Token" here is a cancellation token, not an auth token. Three of them exist, one per kind of
 * long-running work, registered under a fixed name in the `CancellationTokenHandler`:
 *
 * | name           | handed to                                  | via                            |
 * |----------------|--------------------------------------------|--------------------------------|
 * | `app`          | `SyncStarter` / `WebSyncStarter`           | `context.params.token`         |
 * | `commandQueue` | `CommandQueueRunner`                       | `context.params.queueToken`    |
 * | `maintenance`  | `MaintenanceSynchroniser`                  | `context.params.maintenanceToken` |
 *
 * For each one this element cancels the token of the previous run — which stops the data sync,
 * the command queue or the maintenance sync still working from that run — and registers a new
 * one for the elements that follow in this run. That is why it comes right after the
 * `SyncIndicatorPreCloser`: nothing that touches the databases or the server may still run on the
 * old token when the new run starts.
 *
 * The tokens carry more than the cancel flag: the sync services record per-service progress on
 * them (`syncCompleted`, `syncTerminal`, `serviceRepeats`), which the sync indicator and the
 * `SyncStatus` read to know when a run has settled. The `SyncTrigger` and the `DatabaseService`
 * also cancel all three from outside a run — when the session leaves `online`, and before a
 * database is deleted or copied.
 */
@Injectable()
export class TokenHandler implements ISyncElement {
  public get name(): string {
    return Token;
  }

  constructor(@Inject(CancellationTokenHandler) private _cancellationTokenHandler: ICancellationTokenHandler) {}

  public async execute(context: ISyncContext): Promise<SyncElementResult> {
    debug('configuring queue token');
    await this.configureQueueToken(context);
    debug('configuring app token');
    await this.configureAppToken(context);
    debug('configuring maintenance token');
    await this.configureMaintenanceToken(context);
    return { contextId: context.id, jobDone: true };
  }

  private async configureMaintenanceToken(context: ISyncContext) {
    const token = this._cancellationTokenHandler.getToken('maintenance');
    await token.cancel();
    context.params.maintenanceToken = this._cancellationTokenHandler.setupToken('maintenance');
  }

  private async configureAppToken(context: ISyncContext) {
    const token = this._cancellationTokenHandler.getToken('app');
    await token.cancel();
    context.params.token = this._cancellationTokenHandler.setupToken('app');
  }

  private async configureQueueToken(context: ISyncContext) {
    const token = this._cancellationTokenHandler.getToken('commandQueue');
    await token.cancel();
    context.params.queueToken = this._cancellationTokenHandler.setupToken('commandQueue');
  }
}
